import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConfirmDialog,
  DataTable,
  PageBody,
  TableActionsMenu,
  buttonVariants,
  type DataTableColumn,
  type DataTableSort,
} from '@wundo/ui';
import { CircleDollarSign, LogIn, Pencil, Trash2 } from 'lucide-react';
import { ApiError } from '@wundo/client/api.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { UsageCostDialog } from '@/components/organisations/UsageCostDialog.tsx';
import { GuestVisitDialog } from '@/components/organisations/GuestVisitDialog.tsx';
import { OrganisationTypeBadge } from '@/components/organisations/OrganisationTypeBadge.tsx';
import {
  deleteOrganisation,
  listOrganisations,
  type OrganisationDetail,
} from '@/lib/organisations.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export const Route = createFileRoute('/_app/organisations/')({
  staticData: { orgScope: 'none' },
  component: OrganisationsPage,
});

const columns: DataTableColumn<OrganisationDetail>[] = [
  { key: 'name', header: 'Name', sortable: true },
  {
    key: 'type',
    header: 'Type',
    sortable: true,
    render: (organisation) => <OrganisationTypeBadge type={organisation.type} />,
  },
  {
    key: 'created_at',
    header: 'Created At',
    hideBelow: 'mobile',
    sortable: true,
    render: (organisation) => formatDateTime(organisation.created_at),
  },
];

export function OrganisationsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'name', direction: 'asc' });
  const [deleting, setDeleting] = useState<OrganisationDetail | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [costFor, setCostFor] = useState<OrganisationDetail | null>(null);
  const [visiting, setVisiting] = useState<OrganisationDetail | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['organisations', page, sort],
    queryFn: () =>
      listOrganisations({ page, sortBy: sort.key, sortOrder: sort.direction }),
  });

  const remove = useMutation({
    mutationFn: (organisation: OrganisationDetail) => deleteOrganisation(organisation.id),
    onSuccess: async () => {
      setDeleting(null);
      setDeleteError('');
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
    },
    onError: (caught: unknown) => {
      setDeleteError(
        apiErrorMessage(
          caught,
          caught instanceof ApiError && caught.status === 422
            ? 'This organisation has users and cannot be deleted.'
            : 'Failed to delete organisation',
        ),
      );
    },
  });

  return (
    <>
      <PageHeader
        title='Organisations'
        subtitle='Manage your organisations and their settings'
      />

      <PageBody>
        <DataTable
          label='Organisations'
          columns={columns}
          rows={data?.data ?? []}
          rowKey={(organisation) => organisation.id}
          loading={isPending}
          error={isError ? 'Failed to load organisations' : undefined}
          rowHref={(organisation) => `/organisations/${organisation.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          pagination={{ page, pageSize: PAGE_SIZE, total: data?.meta.totalItems ?? 0 }}
          onPageChange={setPage}
          toolbar={
            <Link to='/organisations/create' className={buttonVariants({ size: 'sm' })}>
              Create
            </Link>
          }
          actions={(organisation) => (
            <TableActionsMenu
              label={`Actions for ${organisation.name}`}
              items={[
                {
                  label: 'Work in this organisation',
                  icon: <LogIn />,
                  onSelect: () => setVisiting(organisation),
                },
                {
                  label: 'Cost',
                  icon: <CircleDollarSign />,
                  onSelect: () => setCostFor(organisation),
                },
                {
                  label: 'Edit',
                  icon: <Pencil />,
                  onSelect: () =>
                    void router.navigate({
                      to: '/organisations/$id/edit',
                      params: { id: organisation.id },
                    }),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 />,
                  tone: 'danger',
                  onSelect: () => {
                    setDeleteError('');
                    setDeleting(organisation);
                  },
                },
              ]}
            />
          )}
        />
      </PageBody>

      {costFor && (
        <UsageCostDialog
          organisationId={costFor.id}
          organisationName={costFor.name}
          open
          onOpenChange={(open) => {
            if (!open) setCostFor(null);
          }}
        />
      )}

      {visiting && (
        <GuestVisitDialog
          organisationId={visiting.id}
          organisationName={visiting.name}
          open
          onOpenChange={(open) => {
            if (!open) setVisiting(null);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
            setDeleteError('');
          }
        }}
        title='Delete organisation?'
        description={`Are you sure you want to delete organisation ${deleting?.name ?? ''}?`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError === '' ? undefined : deleteError}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting).catch(() => undefined);
        }}
      />
    </>
  );
}
